import { Dialog, IconButton, ImageList, ImageListItem, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import Slider from 'react-slick';
import ReactPlayer from 'react-player';
import { useInView } from 'react-intersection-observer';
import Icon from '@/components-new/common/icon/Icon';
import { Image } from '@/components-new/index';
import { Props } from './types';

const GalleryDialog = ({ open, setOpen, gallery, title }: Props) => {
  const [openSlider, setOpenSlider] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const sliderRef = useRef<any>(null);

  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleOpenSlider = (index) => {
    setCurrentIndex(index);
    setOpenSlider(true);
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (openSlider) {
      if (e.key === 'ArrowRight') {
        sliderRef.current.slickNext();
      } else if (e.key === 'ArrowLeft') {
        sliderRef.current.slickPrev();
      }
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown, openSlider]);

  useEffect(() => {
    if (sliderRef.current) {
      sliderRef.current.slickGoTo(currentIndex);
    }
  }, [currentIndex, gallery]);

  const handleBeforeChange = (_, newIndex: number) => {
    setCurrentIndex(newIndex);

    const thumbId = `thumbnail-${newIndex}`;
    const thumb = document.getElementById(thumbId);

    if (thumb) {
      thumb.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'center',
      });
    }
  };

  const settings = useMemo(() => {
    return {
      dots: true,
      infinite: true,
      speed: 500,
      slidesToShow: 1,
      slidesToScroll: 1,
      initialSlide: currentIndex,
      autoplay: false,
      customPaging: (i) =>
        gallery[i].dataType === 'video' || gallery[i].dataType === 'galleryVideo' ? (
          <div
            id={`thumbnail-${i}`}
            style={{
              width: 50,
              height: 50,
              padding: 5,
              cursor: 'pointer',
              opacity: currentIndex === i ? 1 : 0.5,
              position: 'relative',
              border: currentIndex === i ? '1px solid #FFFFFF' : '2px solid transparent',
              borderRadius: '8px',
            }}
          >
            <ReactPlayer
              style={{ position: 'relative', width: '100%', height: '100%' }}
              url={gallery[i].dataType === 'video' ? `${process.env.REACT_APP_AWS}/${gallery[i].filePath}` : gallery[i].filePath}
              controls={false}
              width="-webkit-fill-available"
              height={isMobile ? '300px' : '730px'}
              playing={false}
              light
              muted
              config={{
                file: {
                  attributes: {
                    controlsList: 'nofullscreen noremoteplayback nodownload', // Disable fullscreen, remote playback, and download
                    disablePictureInPicture: true, // Disable PiP
                    startTime: 1, // Start at 1 second
                    onLoadedMetadata: (e) => {
                      e.target.currentTime = 1; // Start at the first second
                    },
                  },
                },
              }}
            />
            <Icon
              icon="fa-solid fa-video"
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 1,
                color: '#ffff',
              }}
            />
          </div>
        ) : (
          <Stack
            id={`thumbnail-${i}`}
            style={{
              padding: 5,
              cursor: 'pointer',
              opacity: currentIndex === i ? 1 : 0.5,
              position: 'relative',
              width: '40px !important',
              height: '40px !important',
            }}
          >
            {inView && (
              <Image
                loading="lazy"
                src={
                  gallery[i].url
                    ? `${process.env.REACT_APP_AWS}/tours/${gallery[i].tourId}/images/${gallery[i].url}`
                    : gallery[i].dataType === 'galleryImage'
                    ? gallery[i].filePath
                    : `${process.env.REACT_APP_AWS}/${gallery[i].filePath}`
                }
                alt={gallery[i].url ? `${gallery[i].url}` : `${gallery[i].filePath}`}
                objectFit="cover"
                sx={{
                  width: '42px',
                  height: '42px',
                  borderRadius: '8px',
                  border: currentIndex === i ? '1px solid #FFFFFF' : '2px solid transparent',
                }}
              />
            )}
          </Stack>
        ),
      appendDots: (dots) => (
        <div
          ref={ref}
          style={{
            position: 'fixed',
            left: 0,
            width: '100%',
            height: '100px',
            overflowX: 'auto',
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
            background: '#00000099',
          }}
        >
          <ul
            style={{
              margin: '0 auto',
              display: 'flex',
              whiteSpace: 'nowrap',
              gap: '15px',
              padding: '10px 2px',
              userSelect: 'none',
              width: 'fit-content',
            }}
          >
            {dots}
          </ul>
        </div>
      ),
      beforeChange: (oldIndex, newIndex) => {
        handleBeforeChange(oldIndex, newIndex);
      },
    };
  }, [currentIndex, gallery]);

  return (
    <>
      {openSlider ? (
        <Dialog open={openSlider} fullScreen PaperProps={{ style: { overflow: 'hidden' } }} onKeyDown={(e) => handleKeyDown(e as any)}>
          <Stack direction="row" spacing={2} px={3} py={1} alignItems="center" justifyContent="flex-end">
            <IconButton onClick={() => setOpen(!open)}>
              <Icon icon="fa fa-times" />
            </IconButton>
          </Stack>

          <Stack sx={{ maxWidth: isMobile ? '300px' : '1300px', margin: 'auto', padding: 2 }}>
            <Slider ref={sliderRef} {...settings}>
              {gallery.map((g, index) => (
                <Stack key={g.url ? g.url : g.filePath} sx={{ overflow: 'hidden' }} ref={ref}>
                  {g.dataType === 'video' || g.dataType === 'galleryVideo' ? (
                    <>
                      {inView && (
                        <ReactPlayer
                          url={g.dataType === 'video' ? `${process.env.REACT_APP_AWS}/${g.filePath}` : g.filePath}
                          controls
                          width="-webkit-fill-available"
                          height={isMobile ? '300px' : '730px'}
                          playing={
                            (gallery[currentIndex].dataType === 'video' && currentIndex === index) || (gallery[currentIndex].dataType === 'galleryVideo' && currentIndex === index)
                          }
                          muted
                          config={{
                            file: {
                              attributes: {
                                controlsList: 'nofullscreen noremoteplayback nodownload', // Disable fullscreen, remote playback, and download
                                disablePictureInPicture: true, // Disable PiP
                                // startTime: 1, // Start at 1 second
                                // onLoadedMetadata: (e) => {
                                //   e.target.currentTime = 1; // Start at the first second
                                // },
                              },
                            },
                          }}
                        />
                      )}
                    </>
                  ) : (
                    <>
                      {inView && (
                        <Image
                          loading="lazy"
                          alt={g.url ? `${g.url}` : `${g.filePath}`}
                          src={
                            g.url
                              ? `${process.env.REACT_APP_AWS}/tours/${g.tourId}/images/${g.url}`
                              : g.dataType === 'galleryImage'
                              ? g.additionalInformation.fullsizeUrl
                              : `${process.env.REACT_APP_AWS}/${g.filePath}`
                          }
                          objectFit="contain"
                          sx={{
                            width: isMobile ? '300px' : '1300px',
                            height: isMobile ? '300px' : '730px',
                            margin: 'auto',
                            borderRadius: '10px',
                            marginBottom: '100px',
                          }}
                        />
                      )}
                    </>
                  )}
                </Stack>
              ))}
            </Slider>
          </Stack>
        </Dialog>
      ) : (
        <Dialog open={open} fullWidth maxWidth="lg" PaperProps={{ style: { overflowY: 'hidden', height: '100%' } }}>
          <Stack direction="row" spacing={2} px={3} py={2} alignItems="center" justifyContent="space-between" sx={{ backgroundColor: '#000000CC' }}>
            <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#FFFFFF' }}>{title}</Typography>
            <IconButton onClick={() => setOpen(!open)}>
              <Icon icon="fa fa-times" />
            </IconButton>
          </Stack>

          <ImageList variant="masonry" cols={isMobile ? 2 : 5} gap={8} sx={{ p: 3, overflowY: 'auto', scrollbarWidth: 'none' }} ref={ref}>
            {gallery.map((g, index) => (
              <>
                {inView && (
                  <ImageListItem key={g.url ? g.url : g.filePath} onClick={() => handleOpenSlider(index)} sx={{ cursor: 'pointer', minHeight: '125px' }}>
                    {g.dataType === 'video' || g.dataType === 'galleryVideo' ? (
                      <ReactPlayer
                        url={g.dataType === 'video' ? `${process.env.REACT_APP_AWS}/${g.filePath}` : g.filePath}
                        controls={false}
                        width="-webkit-fill-available"
                        height="auto"
                        muted
                        style={{ borderRadius: '12px', overflow: 'hidden' }}
                        config={{
                          // File player (HTML5 video/audio) configuration
                          file: {
                            attributes: {
                              controlsList: 'nofullscreen noremoteplayback nodownload', // Disable fullscreen, remote playback, and download
                              disablePictureInPicture: true, // Disable PiP
                              startTime: 1, // Start at 1 second
                              onLoadedMetadata: (e) => {
                                e.target.currentTime = 1; // Start at the first second
                              },
                            },
                          },
                        }}
                      />
                    ) : (
                      <Image
                        loading="lazy"
                        alt={g.url ? `${g.url}` : `${g.filePath}`}
                        src={
                          g.url
                            ? `${process.env.REACT_APP_AWS}/tours/${g.tourId}/images/${g.url}`
                            : g.dataType === 'galleryImage'
                            ? g.filePath
                            : `${process.env.REACT_APP_AWS}/${g.filePath}`
                        }
                        objectFit="cover"
                        sx={{
                          width: '100%',
                          height: 'auto',
                          borderRadius: '12px',
                        }}
                      />
                    )}
                  </ImageListItem>
                )}
              </>
            ))}
          </ImageList>
        </Dialog>
      )}
    </>
  );
};

export default GalleryDialog;
